import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@xing-com/button';
import { FormField } from '@xing-com/text-field';
import { Row, Column } from '@xing-com/xing-grid';

import { DATA_TEST_IDS } from './constants';

type AllowedColumnSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

type Filter = {
  id: string;
  labelKey: string;
  helperTextKey: string;
  value?: string;
};

type FiltersFormProps = {
  filters: Filter[];
  onSubmit: (query: Record<string, string>) => void;
  submitLabelKey: string;
  className?: string;
};

export const FiltersForm: React.FC<FiltersFormProps> = ({
  filters,
  onSubmit,
  submitLabelKey,
  className,
}) => {
  const { formatMessage } = useIntl();

  const [filterQuery, setFilterQuery] = useState<Record<string, string>>(
    filters.reduce((prev, filter) => {
      return { ...prev, [filter.id]: filter.value || '' };
    }, {})
  );

  const handleSubmit = (): void => {
    onSubmit(filterQuery);
  };

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const inputColumnSize = Math.floor(10 / filters.length) as AllowedColumnSize;

  return (
    <Row className={className}>
      {filters.map(({ id, labelKey, helperTextKey }) => (
        <Column size={inputColumnSize} key={id}>
          <FormField
            id={id}
            size="small"
            label={formatMessage({ id: labelKey })}
            helperText={formatMessage({
              id: helperTextKey,
            })}
            value={filterQuery[id]}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFilterQuery({ ...filterQuery, [id]: e.target.value });
            }}
            data-test-id={`${DATA_TEST_IDS.filtersForm.input}_${id}`}
          />
        </Column>
      ))}
      <Column size={2}>
        <Button
          variant="primary"
          onClick={handleSubmit}
          data-test-id={DATA_TEST_IDS.filtersForm.button}
        >
          <FormattedMessage id={submitLabelKey} />
        </Button>
      </Column>
    </Row>
  );
};
